import {
    useMutation,
    useQueryClient,
    useQuery,
    QueryClient,
    QueryResult,
    UseMutationResult,
    QueryOptions,
    useSuspenseQuery,
    UseSuspenseQueryOptions,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type {
    Account,
    AccountBSUDetails,
    AccountId,
    AccountNumber,
    AccountProperty,
    AccountWithdrawalDetails,
} from '../generated-types';
import {
    isLoanOrFlexiAccount,
    isNotBlacklisted,
    isNotBSU,
    isNotLoanAccount,
    isValidAccountPaymentFrom,
    isValidAccountTransferFrom,
    isValidAccountTransferTo,
    isValidAvtalegiroFromAccount,
    isValidPaymentAgreementAccount,
} from '../services/accountFilters';
import {
    getAccount,
    getAccountFreeWithdrawals,
    getAccounts,
    getBSUDetails,
    isCloseable,
    updateAccountAlias,
    updateAccountDetails,
} from '../services/accountService';
import getFreeWithdrawalStatus from '../utils/mapper/getFreeWithdrawalStatus';

type QueryOptionsType<T> = {
    // eslint-disable-next-line
    select?: (data: T) => any;
} & Omit<QueryOptions<T>, 'select' | 'queryKey' | 'queryFn'>;

export const ACCOUNTS_QUERY_KEY = ['accounts'];

export const ensureAccountsData = async (queryClient: QueryClient): Promise<Account[]> => {
    try {
        return await queryClient.ensureQueryData<Account[]>({ queryKey: ACCOUNTS_QUERY_KEY });
    } catch {
        return [];
    }
};

export const useSuspendedAccounts = (
    options: Omit<UseSuspenseQueryOptions<Account[], RequestExceptionWithResponse>, 'queryKey' | 'queryFn'> = {},
) =>
    useSuspenseQuery({
        ...options,
        queryKey: ACCOUNTS_QUERY_KEY,
        queryFn: getAccounts,
    });

export function useAccounts<T = Account[]>(options: QueryOptionsType<T> = {}): QueryResult<T> {
    return useQuery<T>({
        ...options,
        queryKey: ACCOUNTS_QUERY_KEY,
        queryFn: () => getAccounts() as unknown as Promise<T>,
    });
}

export const useAccount = (accountId?: AccountId): QueryResult<Account> =>
    useQuery<Account>({
        queryKey: ['account', accountId?.value],
        queryFn: () => getAccount(accountId!),
        enabled: !!accountId?.value,
    });

const byAccountNumberSelector = (accountNumber?: AccountNumber) => (accounts: Account[]) => {
    return accounts?.find((account) => account.accountNumber.value === accountNumber?.value);
};

export const useAccountNumber = (accountNumber?: AccountNumber, enabled = true) => {
    return useAccounts({
        select: byAccountNumberSelector(accountNumber),
        enabled: !!accountNumber?.value && enabled,
    }) as QueryResult<Account | undefined>;
};

export const accountListSelector = (accounts: Account[]) => accounts.filter(isNotBlacklisted);
export const nonBlockedAccountsSelector = (accounts: Account[]) =>
    accounts.filter((a) => isValidAccountPaymentFrom(a) && isValidAccountTransferFrom(a));

export const allLoanAccountsSelector = (accounts: Account[]) => accounts.filter(isLoanOrFlexiAccount);
export const paymentFromSelector = (accounts: Account[]) => accounts.filter(isValidAccountPaymentFrom);
export const salaryAccount = (accounts: Account[]) =>
    accounts
        .filter(isNotBSU)
        .filter(isNotLoanAccount)
        .filter((account) => account.rights.accessPermission);
export const paymentAgreementFromSelector = (accounts: Account[]) => accounts.filter(isValidPaymentAgreementAccount);
export const avtalegiroSelector = (accounts: Account[]) => accounts.filter(isValidAvtalegiroFromAccount);
export const transferToSelector = (accounts: Account[]) => accounts.filter(isValidAccountTransferTo);

export const useUpdateAccounts = () => {
    const queryClient = useQueryClient();

    return (accountIds: AccountId[] = []) => {
        return queryClient.invalidateQueries({
            predicate: (query) => {
                if (query.queryKey[0] === 'account' && query.queryKey[1]) {
                    return accountIds.length === 0 || accountIds.some((a) => a.value === query.queryKey[1]);
                }
                return query.queryKey[0] === 'accounts';
            },
        });
    };
};

export const useUpdateAccountAlias = (
    accountId: AccountId,
): UseMutationResult<unknown, RequestExceptionWithResponse, string> => {
    const updateAccounts = useUpdateAccounts();

    return useMutation<unknown, RequestExceptionWithResponse, string>({
        mutationFn: (alias: string) => updateAccountAlias(accountId, alias),
        onSuccess: () => updateAccounts([accountId]),
    });
};

export const useUpdateOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<unknown, RequestExceptionWithResponse, AccountProperty[]>({
        mutationKey: ['update-accounts-order'],
        mutationFn: updateAccountDetails,
        onSuccess: () => {
            return queryClient.refetchQueries({ queryKey: ACCOUNTS_QUERY_KEY });
        },
    });
};

type AccountWithdrawalReturn = {
    hasTotalNumberOfWithdrawals: boolean;
    showFreewithdrawal: boolean;
    hasPerformedWithdrawals: boolean;
    data: AccountWithdrawalDetails | undefined;
} & Omit<QueryResult<AccountWithdrawalDetails>, 'data'>;

export const useAccountFreeWithdrawals = (account?: Account, enabled = false): AccountWithdrawalReturn => {
    const isEnabled = account && account.additionalInfo?.hasWithdrawalsCondition && enabled;

    const { data: freeWithdrawals, ...freeWithdrawalsState } = useQuery<
        AccountWithdrawalDetails,
        RequestExceptionWithResponse
    >({
        queryKey: ['freeWitdrawals', account?.accountId.value],
        queryFn: () => getAccountFreeWithdrawals(account!.accountId),
        enabled: !!isEnabled,
    });

    const { hasTotalNumberOfWithdrawals, showFreewithdrawal, hasPerformedWithdrawals } =
        getFreeWithdrawalStatus(freeWithdrawals);

    return {
        data: freeWithdrawals,
        ...freeWithdrawalsState,
        hasPerformedWithdrawals,
        hasTotalNumberOfWithdrawals,
        showFreewithdrawal,
    };
};

export const useBSUDetails = (account?: Account, isEnabled = false): QueryResult<AccountBSUDetails> =>
    useQuery({
        queryKey: ['bsudetails', account?.accountId.value],
        queryFn: () => getBSUDetails(account?.accountId),
        enabled: isEnabled && !!account,
    });

export const useIsCloseable = (accountId: AccountId, enabled = true): QueryResult<boolean> =>
    useQuery({
        queryKey: ['isCloseable', accountId],
        queryFn: () => isCloseable(accountId),
        enabled,
    });
